// Indication → target peak/trough ranges (PSD §4.2.15 / TODO Task 0.4, 2.17)
import { Agent, Indication, Strategy, type AgentParams } from '@/types';

/**
 * Suggested default strategy by indication (FE-E11).
 * When the patient's indication has a strong clinical preference for an alternate
 * pathway (e.g. endocarditis → synergy), the UI surfaces a one-click switch button.
 */
export const SUGGESTED_STRATEGY_BY_INDICATION: Partial<Record<Indication, Strategy>> = {
  [Indication.Endocarditis]: Strategy.Synergy,
};

export interface IndicationTarget {
  indication: Indication;
  /** dose intensity multiplier applied to the agent's base mg/kg */
  intensityMultiplier: number;
  targetPeakRange: [number, number];
  targetTroughRange: [number, number];
}

/**
 * Phase 1 standard targets (gent/tobra EI). Phase 2 expands indication-specific.
 * Ranges expressed for gentamicin/tobramycin (mg/L); amikacin scaled ~3×.
 */
export const INDICATION_TARGETS: Record<Indication, IndicationTarget> = {
  [Indication.GramNegativeSepsis]: {
    indication: Indication.GramNegativeSepsis,
    intensityMultiplier: 1.0,
    targetPeakRange: [8, 12],
    targetTroughRange: [0.5, 2],
  },
  [Indication.ComplicatedUTI]: {
    indication: Indication.ComplicatedUTI,
    intensityMultiplier: 1.0,
    targetPeakRange: [6, 10],
    targetTroughRange: [0.5, 2],
  },
  [Indication.Pyelonephritis]: {
    indication: Indication.Pyelonephritis,
    intensityMultiplier: 1.0,
    targetPeakRange: [6, 10],
    targetTroughRange: [0.5, 2],
  },
  [Indication.IntraAbdominal]: {
    indication: Indication.IntraAbdominal,
    intensityMultiplier: 1.0,
    targetPeakRange: [8, 12],
    targetTroughRange: [0.5, 2],
  },
  [Indication.Pneumonia]: {
    indication: Indication.Pneumonia,
    intensityMultiplier: 1.0,
    targetPeakRange: [8, 12],
    targetTroughRange: [0.5, 2],
  },
  [Indication.Endocarditis]: {
    indication: Indication.Endocarditis,
    intensityMultiplier: 0.6, // synergy: lower peak
    targetPeakRange: [3, 4],
    targetTroughRange: [0.5, 1],
  },
  [Indication.Bacteremia]: {
    indication: Indication.Bacteremia,
    intensityMultiplier: 1.0,
    targetPeakRange: [8, 12],
    targetTroughRange: [0.5, 2],
  },
  [Indication.Meningitis]: {
    indication: Indication.Meningitis,
    intensityMultiplier: 1.0,
    targetPeakRange: [8, 12],
    targetTroughRange: [0.5, 2],
  },
  [Indication.FebrileNeutropenia]: {
    indication: Indication.FebrileNeutropenia,
    intensityMultiplier: 1.0,
    targetPeakRange: [8, 12],
    targetTroughRange: [0.5, 2],
  },
  [Indication.Other]: {
    indication: Indication.Other,
    intensityMultiplier: 1.0,
    targetPeakRange: [6, 10],
    targetTroughRange: [0.5, 2],
  },
};

const AMIKACIN_SCALE = 3;

/** Resolve indication-specific targets for an agent/strategy, returning an updated AgentParams. */
export function resolveIndicationTargets(
  params: AgentParams,
  indication: Indication,
  _strategy: Strategy,
): AgentParams {
  const target = INDICATION_TARGETS[indication];
  const isAmikacin = params.agent === Agent.Amikacin;
  const scale = isAmikacin ? AMIKACIN_SCALE : 1;
  const scaledPeak: [number, number] = [target.targetPeakRange[0] * scale, target.targetPeakRange[1] * scale];
  const scaledTrough: [number, number] = [
    target.targetTroughRange[0] * scale,
    target.targetTroughRange[1] * scale,
  ];
  return {
    ...params,
    targetPeakMg: (scaledPeak[0] + scaledPeak[1]) / 2,
    targetTroughMg: (scaledTrough[0] + scaledTrough[1]) / 2,
    targetPeakRange: scaledPeak,
    targetTroughRange: scaledTrough,
    traditionalDoseMgKg: params.traditionalDoseMgKg * target.intensityMultiplier,
  };
}
